<template>

  <div class="reupload-order-documents">

    <!-- (data loading spinner) -->
    <v-container class="d-flex flex-column align-center justify-center" v-if="isDataUpdating">
      <v-progress-circular indeterminate :size="35" style="margin:20px 0" color="primary" />
    </v-container>

    <template v-else>
      <!-- Change Documents Button & Dialog -->
      <v-dialog persistent fullscreen v-model="isDialogOpen" align="center">
        <template v-slot:activator="{ on, attrs }">
          <v-btn :outlined="buttonOutlined" :color="buttonColor" v-bind="attrs" v-on="on">
            {{ buttonText }}
          </v-btn>
        </template>
        <v-card>
          <v-container>
            <v-row class="mt-1 mb-1">
              <v-col align="center">
                <v-btn text @click="isDialogOpen = false">Cancel</v-btn>
                <v-btn color="red darken-3" class="ml-12" @click="saveChanges">Save Changes</v-btn>
              </v-col>
            </v-row>
            <file-attachment-manager
                ref="doc-manager"
                :titleText="titleText"
                fileDisplayWidth="300px"
                :allowedFileTypes="$root.allowedFileTypesForUploads" />
          </v-container>
        </v-card>
      </v-dialog>
      <!-- Info -->
      <div class="mt-3 description-text" v-if="showNote">
        NOTE: Adding new {{ docType }} docs will overwrite the existing ones.
      </div>
    </template>

  </div>

</template>

<script>
import FileAttachmentManager from '@/components/app/FileAttachmentManager'

export default {
  name: 'ReuploadOrderDocuments',

  components: {
    FileAttachmentManager
  },

  data () {
    return {
      orderIdForAssets: null,
      isDataUpdating: false,
      isDialogOpen: false,
      newDocs: null
    }
  },

  props: {
    // The order that owns the documents
    order: {
      type: Object,
      required: true
    },
    // The doc type: "supporting" || 'attachment'
    docType: {
      type: String,
      required: true
    },
    showNote: {
      type: Boolean,
      default: true
    },
    buttonText: {
      type: String,
      default: 'Change Docs'
    },
    buttonOutlined: {
      type: Boolean,
      default: false
    },
    buttonColor: {
      type: String,
      default: 'red darken-3'
    }
  },

  computed: {
    uiType () {
      return this.$root.uiType()
    },
    titleText () {
      return (this.docType === 'supporting')
        ? 'Supporting Documents'
        : 'Attachment Documents'
    }
  },

  beforeMount () {
    // Load order ID for assets
    this.orderIdForAssets = (this.order.root_order_id) ? this.order.root_order_id : this.order.id
  },

  methods: {
    // Handle doc upload changes
    async saveChanges () {
      this.isDialogOpen = false

      // Obtain new docs
      const docManager = this.$refs['doc-manager']
      const docs = (docManager) ? docManager.getAttachments() : null

      if (docs && docs.length > 0) {
        this.isDataUpdating = true
        this.$emit('uploading')
        this.newDocs = docs

        // Upload docs to system
        const results = await this.uploadDocuments()

        // Emit failure scenario, otherwise update order info
        if (results.errors && results.errors.length > 0) {
          this.$emit('failure', results)
        } else {
          try {
            // Build new doc info for order
            const docInfo = {}
            for (const doc of docs) {
              if (doc.asset_key && doc.file) {
                docInfo[doc.asset_key] = {
                  asset_key: doc.asset_key,
                  order: doc.order,
                  label: doc.label,
                  category: doc.category,
                  file_name: doc.file_name,
                  file_type: doc.file_type,
                  file_extension: doc.file_extension
                }
              }
            }

            // Update order
            const bodyData = {}
            bodyData[`${this.docType}_docs`] = docInfo
            await this.$store.dispatch('sh/dataApi', {
              method: 'post',
              uri: `order/${this.order.id}`,
              opts: {
                bodyData
              }
            })

            // DEVING - success/failure examples
            // await new Promise(resolve => {
            //   setTimeout(() => {
            //     resolve({ id: '123', order_number: '#123' })
            //   }, 5000)
            // })

            // Emit success
            this.$emit('success', results)
          } catch (err) {
            const errMessage = err.message || err.text
            console.log(`Error saving new ${this.docType} doc info for order: ${this.order.id} =>`, err)
            this.$emit('failure', { uploaded: 0, errors: [errMessage] })
          }
        }

        this.isDataUpdating = false
      } // end-if (docs && docs.length > 0)
    },

    // Perform upload of new docs
    async uploadDocuments () {
      const errors = []
      let uploaded = 0

      for (const doc of this.newDocs) {
        const assetKey = doc.asset_key
        const file = doc.file

        if (assetKey && file) {
          let uploadInfo = null
          try {
            // Generate upload URL
            uploadInfo = await this.$store.dispatch('sh/dataApi', {
              method: 'get',
              uri: `order/${this.orderIdForAssets}/asset/uploadUrl`,
              to_model: false,
              opts: {
                params: { asset_key: assetKey }
              }
            })

            // Upload document
            const result = await fetch(uploadInfo.url, { method: 'PUT', body: file, signal: AbortSignal.timeout(55000) })

            // DEVING - success/failure examples
            // const result = await new Promise(resolve => {
            //   setTimeout(() => {
            //     resolve({ uploaded: 2, errors: ['New Issue with Attachment 123', 'New Issue with Attachment 345', 'New Issue with Attachment 988123'] })
            //   }, 4000)
            // })

            // Record issues saving to storage
            if (result && result.status !== 200) {
              const issueInfo = `Issue uploading: ${doc.file_name} - ${result.statusText}`
              errors.push(issueInfo)
            } else {
              ++uploaded
            }
          } catch (err) {
            const errMessage = err.message || err.text
            const errInfo = `Error performing upload: ${doc.file_name} - ${errMessage}`
            errors.push(errInfo)
          }
        }
      }

      return { uploaded, errors }
    }
  }
}
</script>

<style scoped>
  .reupload-order-documents .description-text {
    color: #9e9e9e;
  }
</style>
